<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { ElMessageBox, ElNotification } from 'element-plus';
import { useSessionStore } from '@/stores/session.store';
import { assetUrl } from '@/utils/assetUrl';
import {
  fetchHarmonicOrbitalForUniverse,
  fetchHarmonicKnowledge,
  activateHarmonicOrbital,
} from '@/api/harmonicOrbital';
import type { ExposedCapital, HarmonicKnowledgeTier } from '@/api/harmonicOrbital';
import type { HarmonicOrbitalModel } from '@/modules/harmonicOrbital/models/HarmonicOrbitalModel';

/**
 * Bannière de fin de jeu. S'affiche quand l'Harmonic Orbital de l'univers du
 * joueur a été DÉCOUVERT (une flotte a stationné dessus, cf
 * FleetTaskResolver) : propose au joueur de l'ACTIVER pour gagner. Une fois
 * activé, affiche l'état "partie terminée" (gagnée par soi ou par un autre).
 * L'annonce aux autres joueurs passe déjà par l'inbox (toast 60s).
 */
const session = useSessionStore();

const orbital = ref<HarmonicOrbitalModel | null>(null);
const tier = ref<HarmonicKnowledgeTier>('None');
const exposedCapitals = ref<ExposedCapital[]>([]);
const activating = ref(false);

const isWinner = computed(
  () =>
    orbital.value?.activatedByPlayerUniverseId != null &&
    orbital.value.activatedByPlayerUniverseId === session.activePlayerUniverseId,
);

const imageUrl = computed(() =>
  assetUrl('harmonic_orbitals', orbital.value?.imageCode ?? 'harmonic_orbital'),
);

const isActivated = computed(() => orbital.value?.isActivated === true);

// La bannière apparaît dès qu'une capitale résonne (palier Exact atteint par
// quelqu'un), même avant la découverte physique de l'objet.
const visible = computed(
  () => orbital.value != null || exposedCapitals.value.length > 0,
);

// Capitales exposées des autres joueurs (la sienne est signalée à part).
const otherExposed = computed(() => exposedCapitals.value.filter((c) => !c.isSelf));
const selfExposed = computed(() => exposedCapitals.value.some((c) => c.isSelf));

async function load(): Promise<void> {
  const universeId = session.activeUniverseId;
  const puId = session.activePlayerUniverseId;
  if (!universeId) return;
  try {
    orbital.value = await fetchHarmonicOrbitalForUniverse(universeId, true);
  } catch {
    // silencieux : la bannière est un bonus, pas un bloquant.
    orbital.value = null;
  }
  if (puId != null) {
    try {
      const k = await fetchHarmonicKnowledge(puId);
      tier.value = k.tier;
      exposedCapitals.value = k.exposedCapitals;
    } catch {
      tier.value = 'None';
      exposedCapitals.value = [];
    }
  }
}

async function activate(): Promise<void> {
  const o = orbital.value;
  const puId = session.activePlayerUniverseId;
  if (!o || puId == null || o.isActivated) return;

  try {
    await ElMessageBox.confirm(
      'Activer l\'Harmonic Orbital met fin à la partie pour cet univers et vous '
        + 'désigne vainqueur. Cette action est irréversible. Confirmer ?',
      'Activer l\'Harmonic Orbital',
      { type: 'warning', confirmButtonText: 'Activer (Victoire)', cancelButtonText: 'Annuler' },
    );
  } catch {
    return; // annulé
  }

  activating.value = true;
  try {
    orbital.value = await activateHarmonicOrbital(o.id, puId);
    ElNotification({
      title: '🏆 VICTOIRE',
      message: 'Vous avez activé l\'Harmonic Orbital. La porte vers un autre univers '
        + 'est ouverte : votre nom entre dans la légende.',
      type: 'success',
      duration: 0,
    });
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } } };
    ElNotification({
      title: 'Activation impossible',
      message: err.response?.data?.message ?? 'Erreur lors de l\'activation.',
      type: 'error',
      duration: 6000,
    });
    void load(); // resynchronise l'état (peut-être déjà activé par un autre)
  } finally {
    activating.value = false;
  }
}

onMounted(load);
</script>

<template>
  <div v-if="visible" class="ho-banner" :class="{ 'ho-banner--done': isActivated }">
    <img class="ho-banner__img" :src="imageUrl" alt="Harmonic Orbital" />
    <div class="ho-banner__text">
      <template v-if="isActivated && isWinner">
        <strong>🏆 Victoire — vous avez activé l'Harmonic Orbital</strong>
        <span>La partie est terminée pour cet univers.</span>
      </template>
      <template v-else-if="isActivated">
        <strong>Fin de partie — l'Harmonic Orbital a été activé</strong>
        <span>Un autre commandant a franchi le portail le premier.</span>
      </template>
      <template v-else-if="orbital">
        <strong>L'Harmonic Orbital a été découvert</strong>
        <span>Une flotte a atteint le portail inter-univers en {{ orbital.coordinates }}.
          Activez-le pour remporter la partie — un Pont Harmonique achevé est requis.</span>
      </template>
      <template v-else>
        <strong>Une porte résonne dans l'univers</strong>
        <span>L'Harmonic Orbital a été localisé. La course est lancée — les
          capitales qui en connaissent les coordonnées sont désormais exposées.</span>
      </template>

      <p v-if="!isActivated && selfExposed" class="ho-banner__self">
        ⚠ Votre capitale résonne — elle est visible de tous les commandants de l'univers.
      </p>

      <ul v-if="!isActivated && otherExposed.length" class="ho-banner__caps">
        <li v-for="c in otherExposed" :key="c.playerUniverseId">
          <strong>{{ c.login }}</strong>
          <span v-if="c.coordinates" class="ho-banner__coords">{{ c.coordinates }}</span>
          <span v-else class="ho-banner__coords">coordonnées inconnues</span>
        </li>
      </ul>
    </div>
    <el-button
      v-if="orbital && !isActivated"
      type="warning"
      :loading="activating"
      class="ho-banner__cta"
      @click="activate"
    >
      Activer (Victoire)
    </el-button>
  </div>
</template>

<style lang="scss" scoped>
.ho-banner {
  display: flex;
  align-items: center;
  gap: $space-3;
  margin: $space-2 $space-4 0;
  padding: $space-2 $space-3;
  border: 1px solid rgba(230, 162, 60, 0.6);
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(230, 162, 60, 0.18), rgba(120, 80, 200, 0.14));
  box-shadow: 0 0 18px rgba(230, 162, 60, 0.25);

  &--done {
    border-color: rgba(120, 140, 180, 0.5);
    background: rgba(30, 40, 60, 0.5);
    box-shadow: none;
  }
}
.ho-banner__img {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
}
.ho-banner__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;

  strong { font-size: 0.95rem; }
  span { font-size: 0.8rem; opacity: 0.85; }
}
.ho-banner__self {
  margin: 4px 0 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(245, 108, 108, 0.95);
}
.ho-banner__caps {
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  font-size: 0.78rem;

  li {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
  }
  strong { font-weight: 600; }
}
.ho-banner__coords {
  font-family: monospace;
  opacity: 0.8;
}
.ho-banner__cta {
  flex: 0 0 auto;
}
</style>
